import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card } from '@/components/Primitives';
import { Modal, Field } from './Extensions';
import { Plus, Trash, PencilSimple, MagnifyingGlass, PhoneCall, DeviceMobile } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';
import { triggerCall, normalizeNumber } from '@/lib/dialer';

/** Phone number cell with click-to-call button. */
const PhoneCell = ({ number, kind, contactName, testIdPrefix, onDial }) => {
    if (!number) return <span className="font-mono text-xs text-[var(--text-disabled)]">—</span>;
    const Icon = kind === 'mobile' ? DeviceMobile : PhoneCall;
    return (
        <button
            type="button"
            data-testid={`${testIdPrefix}-${kind}-dial`}
            onClick={() => onDial(number, contactName)}
            title={`Zavolat ${number}`}
            className="inline-flex items-center gap-1.5 px-2 py-1 font-mono text-xs group transition-colors"
            style={{
                color: 'var(--text-primary)',
                border: '1px solid transparent',
                borderRadius: 2,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,197,94,0.10)'; e.currentTarget.style.borderColor = 'rgba(34,197,94,0.4)'; e.currentTarget.style.color = '#22c55e'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--text-primary)'; }}
        >
            <Icon size={12} weight="bold" />
            <span>{number}</span>
        </button>
    );
};

const Contacts = () => {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const r = await api.get('/contacts', { params: search ? { search } : {} });
        setItems(r.data);
    };
    useEffect(() => { load(); }, [search]);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing.id) await api.put(`/contacts/${editing.id}`, editing);
            else await api.post('/contacts', editing);
            setEditing(null); load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat kontakt?')) return;
        await api.delete(`/contacts/${id}`); load();
    };

    const dial = async (rawNumber, contactName) => {
        const target = normalizeNumber(rawNumber);
        if (!target) {
            toast({ title: 'Neplatné číslo', variant: 'destructive' });
            return;
        }
        try {
            const { delivered } = await triggerCall(target, { contactName });
            toast({
                title: 'Vytáčí se',
                description: delivered === 'in-context'
                    ? `${contactName || target} · v softphonu`
                    : `${contactName || target} · otevírám softphone…`,
            });
        } catch (e) {
            toast({ title: 'Chyba', description: e.message, variant: 'destructive' });
        }
    };

    /** Phone number cell with click-to-call button. */

    return (
        <>
            <PageHeader
                eyebrow="CRM"
                title="Telefonní seznam"
                description="Centrální seznam kontaktů. Při příchozím hovoru se kontakt automaticky páruje s caller ID."
                actions={
                    <button onClick={() => setEditing({ first_name: '', last_name: '', company: '', primary_phone: '', mobile_phone: '', email: '', tags: [], notes: '' })}
                            data-testid="contact-create-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nový kontakt
                    </button>
                }
            />
            <Card>
                <div className="flex items-center gap-3 mb-4">
                    <MagnifyingGlass size={16} color="var(--text-secondary)" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                           data-testid="contact-search"
                           placeholder="Hledat podle jména, firmy, telefonu nebo e-mailu…"
                           className="flex-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                </div>
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné kontakty.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Jméno</th>
                                <th className="text-left">Firma</th>
                                <th className="text-left">Telefon</th>
                                <th className="text-left">Mobil</th>
                                <th className="text-left">E-mail</th>
                                <th className="text-left">Tagy</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2">{c.full_name || `${c.first_name || ''} ${c.last_name || ''}`}</td>
                                    <td className="text-xs">{c.company || '—'}</td>
                                    <td>
                                        <PhoneCell number={c.primary_phone} kind="primary"
                                                   contactName={c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                                                   testIdPrefix={`contact-${c.id}`}
                                                   onDial={dial} />
                                    </td>
                                    <td>
                                        <PhoneCell number={c.mobile_phone} kind="mobile"
                                                   contactName={c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                                                   testIdPrefix={`contact-${c.id}`}
                                                   onDial={dial} />
                                    </td>
                                    <td className="font-mono text-xs">{c.email || '—'}</td>
                                    <td className="text-xs">{(c.tags || []).join(', ')}</td>
                                    <td className="text-right">
                                        <button onClick={() => setEditing({ ...c })} className="p-1 hover:bg-[var(--bg-surface-hover)]" data-testid={`contact-edit-${c.id}`}><PencilSimple size={14} /></button>
                                        <button onClick={() => del(c.id)} className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1" data-testid={`contact-delete-${c.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal title="Kontakt" onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-2 gap-3">
                        <Field label="Jméno" value={editing.first_name || ''} onChange={(v) => setEditing({ ...editing, first_name: v })} />
                        <Field label="Příjmení" value={editing.last_name || ''} onChange={(v) => setEditing({ ...editing, last_name: v })} />
                        <Field label="Firma" value={editing.company || ''} onChange={(v) => setEditing({ ...editing, company: v })} />
                        <Field label="E-mail" value={editing.email || ''} onChange={(v) => setEditing({ ...editing, email: v })} type="email" mono />
                        <Field label="Telefon (primární)" value={editing.primary_phone} onChange={(v) => setEditing({ ...editing, primary_phone: v })} required mono />
                        <Field label="Mobil" value={editing.mobile_phone || ''} onChange={(v) => setEditing({ ...editing, mobile_phone: v })} mono />
                        <div className="col-span-2">
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Tagy (čárka)</label>
                            <input value={(editing.tags || []).join(',')}
                                   onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Poznámky</label>
                            <textarea rows={2} value={editing.notes || ''}
                                      onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                      className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                      style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div className="col-span-2 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-xs font-bold uppercase border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid="contact-save-btn" className="px-3 py-2 text-xs font-bold uppercase" style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default Contacts;
